var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import ArrowLeft from './components/ArrowLeft';
import ArrowRight from './components/ArrowRight';
import CloseIcon from './components/CloseIcon';
import PreventScroll from './components/PreventScroll';
import SlidePortal from './components/SlidePortal';
import useAdjacentImages from './hooks/useAdjacentImages';
import useAnimationVisible from './hooks/useAnimationVisible';
import useEventListener from './hooks/useEventListener';
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect';
import useMethods from './hooks/useMethods';
import useSetState from './hooks/useSetState';
import PhotoBox from './PhotoBox';
import './PhotoSlider.css';
import isTouchDevice from './utils/isTouchDevice';
import { limitNumber } from './utils/limitTarget';
import { defaultEasing, defaultOpacity, defaultSpeed, horizontalOffset, maxMoveOffset } from './variables';
var initialState = {
    x: 0,
    touched: false,
    pause: false,
    lastCX: undefined,
    lastCY: undefined,
    bg: undefined,
    lastBg: undefined,
    overlay: true,
    minimal: true,
    scale: 1,
    rotate: 0,
};
export default function PhotoSlider(props) {
    var _a = props.loop, loop = _a === void 0 ? 3 : _a, speedFn = props.speed, easingFn = props.easing, photoClosable = props.photoClosable, _b = props.maskClosable, maskClosable = _b === void 0 ? true : _b, _c = props.maskOpacity, maskOpacity = _c === void 0 ? defaultOpacity : _c, _d = props.pullClosable, pullClosable = _d === void 0 ? true : _d, _e = props.bannerVisible, bannerVisible = _e === void 0 ? true : _e, overlayRender = props.overlayRender, toolbarRender = props.toolbarRender, className = props.className, maskClassName = props.maskClassName, photoClassName = props.photoClassName, photoWrapClassName = props.photoWrapClassName, loadingElement = props.loadingElement, brokenElement = props.brokenElement, images = props.images, _f = props.index, controlledIndex = _f === void 0 ? 0 : _f, controlledIndexChange = props.onIndexChange, visible = props.visible, onClose = props.onClose, afterClose = props.afterClose, portalContainer = props.portalContainer;
    var _g = __read(useSetState(initialState), 2), state = _g[0], updateState = _g[1];
    var _h = __read(useState(0), 2), innerIndex = _h[0], updateInnerIndex = _h[1];
    var x = state.x, touched = state.touched, pause = state.pause, lastCX = state.lastCX, lastCY = state.lastCY, _j = state.bg, bg = _j === void 0 ? maskOpacity : _j, lastBg = state.lastBg, overlay = state.overlay, minimal = state.minimal, scale = state.scale, rotate = state.rotate, onScale = state.onScale, onRotate = state.onRotate;
    // 受控 index
    // eslint-disable-next-line no-prototype-builtins
    var isControlled = props.hasOwnProperty('index');
    var index = isControlled ? controlledIndex : innerIndex;
    var onIndexChange = isControlled ? controlledIndexChange : updateInnerIndex;
    // 内部虚拟 index
    var virtualIndexRef = useRef(index);
    // 当前图片
    var imageLength = images.length;
    var currentImage = images[index];
    // 是否开启
    // noinspection SuspiciousTypeOfGuard
    var enableLoop = typeof loop === 'boolean' ? loop : imageLength > loop;
    // 显示动画处理
    var _k = __read(useAnimationVisible(visible, afterClose), 3), realVisible = _k[0], activeAnimation = _k[1], onAnimationEnd = _k[2];
    useIsomorphicLayoutEffect(function () {
        // 显示弹出层，修正正确的指向
        if (realVisible) {
            updateState({
                pause: true,
                x: index * -(window.innerWidth + horizontalOffset),
            });
            virtualIndexRef.current = index;
            return;
        }
        // 关闭后清空状态
        updateState(initialState);
    }, [realVisible]);
    var _l = useMethods({
        close: function (evt) {
            if (onRotate) {
                onRotate(0);
            }
            updateState({
                overlay: true,
                // 记录当前关闭时的透明度
                lastBg: bg,
            });
            onClose(evt);
        },
        changeIndex: function (nextIndex, isPause) {
            if (isPause === void 0) { isPause = false; }
            // 当前索引
            var currentIndex = enableLoop ? virtualIndexRef.current + (nextIndex - index) : nextIndex;
            var max = imageLength - 1;
            // 虚拟 index
            // 非循环模式，限制区间
            var limitIndex = limitNumber(currentIndex, 0, max);
            var nextVirtualIndex = enableLoop ? currentIndex : limitIndex;
            // 单个屏幕宽度
            var singlePageWidth = window.innerWidth + horizontalOffset;
            updateState({
                touched: false,
                lastCX: undefined,
                lastCY: undefined,
                x: -singlePageWidth * nextVirtualIndex,
                pause: isPause,
            });
            virtualIndexRef.current = nextVirtualIndex;
            // 更新真实的 index
            var realLoopIndex = nextIndex < 0 ? max : nextIndex > max ? 0 : nextIndex;
            if (onIndexChange) {
                onIndexChange(enableLoop ? realLoopIndex : limitIndex);
            }
        },
    }), close = _l.close, changeIndex = _l.changeIndex;
    useEventListener('keydown', function (evt) {
        if (visible) {
            switch (evt.key) {
                case 'ArrowLeft':
                    changeIndex(index - 1, true);
                    break;
                case 'ArrowRight':
                    changeIndex(index + 1, true);
                    break;
                case 'Escape':
                    close();
                    break;
            }
        }
    });
    function handlePhotoTap(closeable) {
        return closeable ? close() : updateState({ overlay: !overlay });
    }
    function handleResize() {
        updateState({
            x: -(window.innerWidth + horizontalOffset) * index,
            lastCX: undefined,
            lastCY: undefined,
            pause: true,
        });
        virtualIndexRef.current = index;
    }
    function handleReachVerticalMove(clientY, nextScale) {
        if (lastCY === undefined) {
            updateState({
                touched: true,
                lastCY: clientY,
                bg: bg,
                minimal: true,
            });
            return;
        }
        var opacity = maskOpacity === null ? null : limitNumber(maskOpacity, 0.01, maskOpacity - Math.abs(clientY - lastCY) / 100 / 4);
        updateState({
            touched: true,
            lastCY: lastCY,
            bg: nextScale === 1 ? opacity : maskOpacity,
            minimal: nextScale === 1,
        });
    }
    function handleReachHorizontalMove(clientX) {
        if (lastCX === undefined) {
            updateState({
                touched: true,
                lastCX: clientX,
                x: x,
                pause: false,
            });
            return;
        }
        var originOffsetClientX = clientX - lastCX;
        var offsetClientX = originOffsetClientX;
        // 第一张和最后一张超出距离减半
        if (!enableLoop && ((index === 0 && originOffsetClientX > 0) || (index === imageLength - 1 && originOffsetClientX < 0))) {
            offsetClientX = originOffsetClientX / 2;
        }
        updateState({
            touched: true,
            lastCX: lastCX,
            x: -(window.innerWidth + horizontalOffset) * virtualIndexRef.current + offsetClientX,
            pause: false,
        });
    }
    function handleReachMove(reachPosition, clientX, clientY, nextScale) {
        if (reachPosition === 'x') {
            handleReachHorizontalMove(clientX);
        }
        else if (reachPosition === 'y') {
            handleReachVerticalMove(clientY, nextScale);
        }
    }
    function handleReachUp(clientX, clientY) {
        var offsetClientX = clientX - (lastCX !== null && lastCX !== void 0 ? lastCX : clientX);
        var offsetClientY = clientY - (lastCY !== null && lastCY !== void 0 ? lastCY : clientY);
        var willClose = false;
        // 下一张
        if (offsetClientX < -maxMoveOffset) {
            changeIndex(index + 1);
            return;
        }
        // 上一张
        if (offsetClientX > maxMoveOffset) {
            changeIndex(index - 1);
            return;
        }
        var singlePageWidth = window.innerWidth + horizontalOffset;
        // 当前偏移
        var currentTranslateX = -singlePageWidth * virtualIndexRef.current;
        if (Math.abs(offsetClientY) > 100 && minimal && pullClosable) {
            willClose = true;
            close();
        }
        updateState({
            touched: false,
            x: currentTranslateX,
            lastCX: undefined,
            lastCY: undefined,
            bg: maskOpacity,
            overlay: willClose ? true : overlay,
        });
    }
    // 截取相邻的图片
    var adjacentImages = useAdjacentImages(images, index, enableLoop);
    if (!realVisible) {
        return null;
    }
    var currentOverlayVisible = overlay && !activeAnimation;
    // 关闭过程中使用下拉保存的透明度
    var currentOpacity = visible ? bg : lastBg;
    // 覆盖物参数
    var overlayParams = onScale &&
        onRotate && {
        images: images,
        index: index,
        visible: visible,
        onClose: close,
        onIndexChange: changeIndex,
        overlayVisible: currentOverlayVisible,
        overlay: currentImage && currentImage.overlay,
        scale: scale,
        rotate: rotate,
        onScale: onScale,
        onRotate: onRotate,
    };
    // 动画时间
    var currentSpeed = speedFn ? speedFn(activeAnimation) : defaultSpeed;
    var currentEasing = easingFn ? easingFn(activeAnimation) : defaultEasing;
    var slideSpeed = speedFn ? speedFn(3) : defaultSpeed + 200;
    var slideEasing = easingFn ? easingFn(3) : defaultEasing;
    return (_jsxs(SlidePortal, __assign({ className: "PhotoView-Portal".concat(!currentOverlayVisible ? ' PhotoView-Slider__clean' : '').concat(!visible ? ' PhotoView-Slider__willClose' : '').concat(className ? " ".concat(className) : ''), role: "dialog", onClick: function (e) { return e.stopPropagation(); }, container: portalContainer }, { children: [visible && _jsx(PreventScroll, {}), _jsx("div", { className: "PhotoView-Slider__Backdrop".concat(maskClassName ? " ".concat(maskClassName) : '').concat(activeAnimation === 1 ? ' PhotoView-Slider__fadeIn' : activeAnimation === 2 ? ' PhotoView-Slider__fadeOut' : ''), style: {
                    background: currentOpacity ? "rgba(0, 0, 0, ".concat(currentOpacity, ")") : undefined,
                    transitionTimingFunction: currentEasing,
                    transitionDuration: "".concat(touched ? 0 : currentSpeed, "ms"),
                    animationDuration: "".concat(currentSpeed, "ms"),
                }, onAnimationEnd: onAnimationEnd }), bannerVisible && (_jsxs("div", __assign({ className: "PhotoView-Slider__BannerWrap" }, { children: [_jsx("div", { className: "PhotoView-Slider__Counter" }), _jsxs("div", __assign({ className: "PhotoView-Slider__BannerRight" }, { children: [toolbarRender && overlayParams && toolbarRender(overlayParams), _jsx(CloseIcon, { className: "PhotoView-Slider__toolbarIcon", onClick: close })] }))] }))), adjacentImages.map(function (item, currentIndex) {
                // 截取之前的索引位置
                var nextIndex = !enableLoop && index === 0 ? index + currentIndex : virtualIndexRef.current - 1 + currentIndex;
                return (_jsx(PhotoBox, { item: item, speed: currentSpeed, easing: currentEasing, visible: visible, onReachMove: handleReachMove, onReachUp: handleReachUp, onPhotoTap: function () { return handlePhotoTap(photoClosable); }, onMaskTap: function () { return handlePhotoTap(maskClosable); }, wrapClassName: photoWrapClassName, className: photoClassName, style: {
                        left: "".concat((window.innerWidth + horizontalOffset) * nextIndex, "px"),
                        transform: "translate3d(".concat(x, "px, 0px, 0)"),
                        transition: touched || pause ? undefined : "transform ".concat(slideSpeed, "ms ").concat(slideEasing),
                    }, loadingElement: loadingElement, brokenElement: brokenElement, onPhotoResize: handleResize, isActive: (currentImage && currentImage.key) === item.key, expose: updateState }, enableLoop ? "".concat(item.key, "/").concat(item.src, "/").concat(nextIndex) : item.key));
            }), !isTouchDevice && bannerVisible && (_jsxs(_Fragment, { children: [(enableLoop || index !== 0) && (_jsx("div", __assign({ className: "PhotoView-Slider__ArrowLeft", onClick: function () { return changeIndex(index - 1, true); } }, { children: _jsx(ArrowLeft, {}) }))), (enableLoop || index + 1 < imageLength) && (_jsx("div", __assign({ className: "PhotoView-Slider__ArrowRight", onClick: function () { return changeIndex(index + 1, true); } }, { children: _jsx(ArrowRight, {}) })))] })), overlayRender && overlayParams && _jsx("div", __assign({ className: "PhotoView-Slider__Overlay" }, { children: overlayRender(overlayParams) }))] })));
}
